import { IconDownload, IconRefresh, IconX } from "@tabler/icons-preact";
import { useEffect, useState } from "preact/hooks";
import { useRegisterSW } from "virtual:pwa-register/preact";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function PwaPrompt() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      setRegistration(registration);
      void registration.update();
    },
    onRegisterError(error) {
      console.error("Falha ao registrar o service worker", error);
    },
  });

  useEffect(() => {
    if (!registration) return;

    const checkForUpdate = () => {
      void registration.update().catch(() => {
        // Estar offline não deve interromper o uso da caixa já instalada.
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    const interval = window.setInterval(checkForUpdate, 5 * 60 * 1000);

    window.addEventListener("focus", checkForUpdate);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", checkForUpdate);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [registration]);

  useEffect(() => {
    const handleInstallable = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setInstallPrompt(installEvent);
    };
    const handleInstalled = () => setInstallPrompt(null);
    window.addEventListener("beforeinstallprompt", handleInstallable);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallable);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  if (!installPrompt && !offlineReady && !needRefresh) return null;

  const close = () => {
    if (needRefresh) setNeedRefresh(false);
    else if (installPrompt) setInstallPrompt(null);
    else setOfflineReady(false);
  };

  const install = async () => {
    if (!installPrompt || installing) return;
    setInstalling(true);
    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } finally {
      setInstalling(false);
    }
  };

  const applyUpdate = async () => {
    if (updating) return;

    setUpdating(true);
    setUpdateError(false);

    let reloadTimer: number | undefined;
    let reloading = false;
    const reload = () => {
      if (reloading) return;
      reloading = true;
      if (reloadTimer) window.clearTimeout(reloadTimer);
      window.location.reload();
    };
    const handleControllerChange = () => reload();

    try {
      navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange, { once: true });

      const currentRegistration = registration ?? await navigator.serviceWorker.getRegistration();
      if (currentRegistration) {
        await currentRegistration.update();
      }

      const waitingWorker = currentRegistration?.waiting;
      if (waitingWorker) {
        waitingWorker.postMessage({ type: "SKIP_WAITING" });
      } else {
        await updateServiceWorker(false);
      }

      // Em alguns navegadores o evento `controllerchange` pode não chegar à
      // aba atual. A recarga de segurança garante que a nova versão seja
      // aberta mesmo nesses casos.
      reloadTimer = window.setTimeout(reload, 3_000);
    } catch (error) {
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
      if (reloadTimer) window.clearTimeout(reloadTimer);
      console.error("Falha ao aplicar a atualização do PowerMail", error);
      setUpdating(false);
      setUpdateError(true);
    }
  };

  const title = needRefresh ? "Atualização disponível" : installPrompt ? "Instale o PowerMail" : "Aplicativo pronto";
  const description = needRefresh
    ? updateError
      ? "Não foi possível atualizar. Verifique sua conexão e tente novamente."
      : updating
        ? "Aplicando a nova versão. O PowerMail será reaberto em instantes."
        : "Atualize para usar a versão mais recente do PowerMail."
    : installPrompt
      ? "Abra sua caixa como aplicativo, com acesso rápido e experiência otimizada."
      : "O shell agora abre mesmo sem conexão.";

  return (
    <aside className={`pwa-prompt${needRefresh ? " pwa-prompt--refresh" : ""}`} role="status" aria-live="polite">
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
      <div className="pwa-prompt__actions">
        {needRefresh ? (
          <button className="button button--primary button--small" type="button" onClick={() => void applyUpdate()} disabled={updating}>
            <IconRefresh className={updating ? "is-spinning" : undefined} size={16} aria-hidden="true" />
            {updating ? "Atualizando…" : updateError ? "Tentar novamente" : "Atualizar"}
          </button>
        ) : installPrompt ? (
          <button className="button button--primary button--small" type="button" onClick={() => void install()} disabled={installing}>
            <IconDownload size={16} aria-hidden="true" />
            {installing ? "Abrindo…" : "Instalar"}
          </button>
        ) : null}
        <button className="icon-button" type="button" onClick={close} aria-label="Fechar aviso">
          <IconX size={18} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
